import { useState } from 'react';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border-b border-[#d4d4ce] last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-6 flex items-center justify-between text-left hover:text-[#c9a84c] transition-colors cursor-pointer"
      >
        <h3 className="text-xl font-semibold pr-8">{question}</h3>
        <i
          className={`ri-arrow-down-s-line text-2xl transition-transform duration-300 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
        ></i>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-lg leading-relaxed text-[#2a2826]">{answer}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Can one person move an upright piano in London?',
      answer:
        'No, one person should never attempt to move an upright piano in London. Even the smallest spinet upright weighs 90kg, while full-size overstrung uprights can weigh 300kg. A minimum of two trained specialists is required for safe upright piano removal. Professional piano movers use specialist equipment including piano boards, straps, and protective blankets. Attempting to move an upright piano alone risks serious personal injury and damage to the instrument worth thousands of pounds.',
    },
    {
      question: 'Will upright piano removal damage my floors?',
      answer:
        'Professional upright piano removal should not damage your floors when performed by specialists. Reputable piano movers in London use protective floor coverings, piano boards with rubber wheels, and proper lifting techniques to prevent floor damage. The main risk comes from dragging a piano across floors or using castors on delicate surfaces like hardwood or laminate. Always inform your piano removal company about your floor type in advance so they can bring appropriate protection.',
    },
    {
      question: 'My upright piano hasn\'t been moved in 20 years — is that a problem?',
      answer:
        'An upright piano that hasn\'t been moved in 20 years requires extra care but can be safely relocated by specialist piano movers in London. Long-stationary pianos may have settled into the floor, have seized castors, or accumulated internal dust affecting the action. Professional movers will inspect the piano before moving, check for structural issues, and may recommend a piano technician inspection if the instrument is particularly valuable or old. The piano will likely need tuning 2-3 weeks after the move to allow it to acclimatise to its new environment.',
    },
  ];

  return (
    <section id="faq" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-center mb-12 text-[#1a1916]">
          FAQ — Upright Piano Removal London
        </h2>
        <div className="bg-[#faf9f5] rounded-lg p-8 shadow-md">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}